import {useCallback} from 'react';

import {useMutation} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {Tag} from 'shells/tags-editor/tags-editor';

import type {TagColor} from 'nutshell-graphql-types';
import type {
    AccountPageFragment,
    AccountPageFragment_tagMaps_edges_node_creator,
    AccountUpdateInput,
    UpdateAccount as UpdateAccountMutation,
    UpdateAccountVariables as UpdateAccountVariables,
} from 'nutshell-graphql-types';

import ACCOUNT_PAGE_FRAGMENT from '../fragments/account-page-fragment.graphql';
import UPDATE_ACCOUNT from '../mutations/update-account.graphql';

type OptimisticNames = {
    territoryName?: string | null;
    accountTypeName?: string | null;
    industryName?: string | null;
};

export const useUpdateAccount = (): {
    updateAccount: (
        input: AccountUpdateInput,
        accountData?: AccountPageFragment | null | undefined,
        optimisticNames?: OptimisticNames,
        optimisticTags?: Tag[]
    ) => Promise<any>;
    isLoading: boolean;
    error: string | null | undefined;
} => {
    const [updateAccount, {loading, error}] = useMutation<
        UpdateAccountMutation,
        UpdateAccountVariables
    >(UPDATE_ACCOUNT);

    const updateAccountWithOptimisticResponse = useCallback(
        (
            input: AccountUpdateInput,
            accountData?: AccountPageFragment | null,
            optimisticNames?: OptimisticNames,
            optimisticTags?: Tag[]
        ) => {
            if (!accountData || !hasOptimsticFields(input)) {
                return updateAccount({variables: {input}});
            }

            const updatedAccount = getUpdatedAccountFromInput(
                input,
                accountData,
                optimisticNames,
                optimisticTags
            );

            return updateAccount({
                variables: {input},
                optimisticResponse: {
                    // @ts-expect-error
                    __typename: 'Mutation',
                    accountUpdate: {
                        __typename: 'AccountUpdatePayload',
                        account: updatedAccount,
                    },
                },
                update: (proxy, mutationResults) => {
                    const account =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.accountUpdate &&
                        mutationResults.data.accountUpdate.account;

                    if (!account) {
                        return;
                    }

                    proxy.writeFragment({
                        id: input.accountId,
                        fragment: ACCOUNT_PAGE_FRAGMENT,
                        fragmentName: 'AccountPageFragment',
                        data: {
                            ...account,
                        },
                    });
                },
            });
        },
        [updateAccount]
    );

    return {
        updateAccount: updateAccountWithOptimisticResponse,
        isLoading: loading,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
    };
};

const getUpdatedAccountFromInput = (
    input: AccountUpdateInput,
    accountData: AccountPageFragment,
    optimisticNames?: OptimisticNames,
    optimisticTags?: Tag[]
): AccountPageFragment => {
    const updatedAccount: AccountPageFragment = {...accountData};

    if (typeof input.name === 'string') {
        updatedAccount.name = input.name;
    }

    if ('assigneeId' in input && input.assigneeId) {
        updatedAccount.owner = {
            id: input.assigneeId,
            __typename: input.assigneeId.includes('users') ? 'User' : 'Team',
        };
    }

    if ('isDeleted' in input) {
        updatedAccount.isDeleted = Boolean(input.isDeleted);
    }

    if ('description' in input && typeof input.description === 'string') {
        updatedAccount.description = input.description;
    }

    if ('territoryId' in input && input.territoryId && optimisticNames?.territoryName) {
        updatedAccount.territory = {
            id: input.territoryId,
            name: optimisticNames.territoryName,
            __typename: 'Territory' as const,
        };
    }

    if ('accountTypeId' in input && input.accountTypeId && optimisticNames?.accountTypeName) {
        updatedAccount.accountType = {
            id: input.accountTypeId,
            name: optimisticNames.accountTypeName,
            __typename: 'AccountType' as const,
        };
    }

    if ('industryId' in input && input.industryId && optimisticNames?.industryName) {
        updatedAccount.industry = {
            id: input.industryId,
            name: optimisticNames.industryName,
            __typename: 'NutshellIndustry' as const,
        };
    }

    if ('employeeCount' in input) {
        updatedAccount.employeeCount =
            input.employeeCount == null ? null : String(input.employeeCount);
    }

    if ('revenue' in input) {
        updatedAccount.revenue =
            input.revenue == null
                ? null
                : {
                      __typename: 'EntityAttributeValueCurrency' as const,
                      currencyValue: {
                          __typename: 'Money' as const,
                          amount: input.revenue,
                      },
                  };
    }

    if ('tags' in input && input.tags && optimisticTags) {
        updatedAccount.tagMaps = {
            __typename: 'TagMapsConnection',
            edges: optimisticTags.map((tag) => ({
                __typename: 'TagMapsEdge',
                node: {
                    __typename: 'Tag',
                    id: tag.id,
                    name: tag.name,
                    count: tag.count ?? 0,
                    tagType: 'ACCOUNT',
                    color: tag.color as TagColor,
                    createdTime: tag.createdTime ?? null,
                    creator: getOptimisticTagCreator(tag, accountData),
                    lastUsedTime: null,
                },
            })),
        };
    }

    return updatedAccount;
};

const getOptimisticTagCreator = (
    tag: Tag,
    accountData: AccountPageFragment
): AccountPageFragment_tagMaps_edges_node_creator => {
    const existingCreator = accountData.tagMaps?.edges.find((edge) => edge.node?.id === tag.id)
        ?.node?.creator;

    if (existingCreator) {
        return existingCreator;
    }

    // If the creator is coming from the dropdown, we need to guarantee it's not null
    if (tag.creator?.__typename === 'Origin') {
        return {
            __typename: 'Origin',
            id: tag.creator.id,
            name: tag.creator.name,
        };
    }

    return {
        __typename: 'User',
        id: tag.creator?.id ?? '',
        name: tag.creator?.name ?? '',
    };
};

const hasOptimsticFields = (input: AccountUpdateInput) => {
    return (
        'name' in input ||
        'assigneeId' in input ||
        'isDeleted' in input ||
        'description' in input ||
        'territoryId' in input ||
        'accountTypeId' in input ||
        'industryId' in input ||
        'employeeCount' in input ||
        'revenue' in input ||
        'tags' in input
    );
};
